import { useContext, useEffect, useMemo, useState } from 'react';
import { SelectedValuesDto } from '../api/backendApi';
import { getConfig } from '../config/envconfig';
import { CalculationDashboard } from '../shared/components/calculation/CalculationDashboard';
import { CALCULATION_STATUS } from '../shared/components/calculation/CalculationResult.types';
import SizeSelection from '../shared/components/unitconfiguration/SizeSelection';
import {
    getProductVariantSizesWithinAirflowRange,
    getClosestAirflowsForSizeOutOfRange,
    sizeIsWithinAirflowRange
} from '../shared/components/unitconfiguration/sizeSelection.functions';
import { WEBSOCKET_ACTIONS } from '../shared/components/websocket/WebsocketWorker.types';
import EventStore from '../shared/components/websocket/eventStore';
import WebsocketWorker from '../shared/components/websocket/websocket.worker';
import { AuthContext } from '../shared/contexts/AuthenticationContext';
import { guard } from '../shared/utility/guard';
import {
    AirflowUnit,
    AirflowUnitKey,
    AirHandlingUnitLite,
    Damper,
    PressureDropUnit
} from '../types/AirHandlingUnitLite.types';
import ContactAndCustomize from './ContactAndCustomize';
import { EmailData } from './ContactForm';
import { ContentContainer, LeftContainer, RightContainer } from './Home.styles';
import InputFieldsContainer, {
    Coil,
    SelectedCountryAndCity
} from './InputFieldsContainer';
import { Header } from './Header';
import {
    HeaderItem,
    HeaderItemDivider,
    HeaderItemsContainer
} from './Header.styles';
import Print from './Print';
import { CalculationResultChangeEvent } from '../shared/components/calculation/CalculationEventWrapper';
import { STORAGE_CALC_JOB_ID_KEY } from '../shared/components/common/constants';
import {
    addCalculationJobIdToSessionStorage,
    clearCalculationJobIdFromSessionStorage,
    getCalculationJobIdFromSessionStorage
} from '../shared/utility/sessionStorage';
import { calculationResultIsOKWithWarningsAndErrors } from '../shared/components/calculation/calculation.functions';
import { errorToastr, successToastr } from '../shared/utility/toastr';
import { Context as TranslationContext } from '../shared/contexts/TranslationContext';
import { BackendApiContext } from '../shared/contexts/BackendApiContext';
import { useProductVariantData } from '../api/productVariant';
import { useClimateLocations } from '../api/climate';
import Loading from '../shared/components/common/Loading';
import MissingDataPage from './MissingDataPage';
import {
    ProductVariantSizesDto,
    SimpleProductVariantSize
} from '../types/ProductVariant.types';
import ProductVariantBasicInfo from './ProductVariantBasicInfo';
import {
    getLiteQueryParams,
    ProductVariantDataSearchParamsForShare
} from './Home.functions';
import UnsupportedScreenSize from './UnsupportedScreenSize';
import UnitSelector from './UnitSelector';
import Conversion from '../shared/utility/conversion';
import { MeasurementUnitsDto } from '@swegon/ahud-buildingblocks-utility';

export type PrintOption = {
    value: string;
    name: string;
};

/**
 * Key is the unit used in the query parameters.
 * Value is our internal unit key.
 * */
const AirflowUnitMap: Record<string, AirflowUnitKey> = {
    cfm: 'feet3_min',
    ls: 'l_s',
    m3h: 'm3_h',
    m3s: 'm3_s'
};

/**
 * These are the countryKeys for the markets available on Swegon.com
 * */
const contactFormMarkets: number[] = [
    5000, 6000, 7000, 8000, 9000, 13000, 14000, 17000, 18000, 21000, 23000,
    32000, 55000, 56000
];
const defaultUnitName = 'GOLD_F_PX';
const defaultAirflowUnit: AirflowUnit = { translated: 'm³/s', key: 'm3_s' };
const defaultPressureUnit: PressureDropUnit = { translated: 'Pa', key: 'Pa' };
const NO_COUNTRY_OR_CITY_SET_YET = 0;

export type InputFields = {
    /** Airflow in unit 'airflowUnit' */
    supplyAirflow: number;
    /** Airflow in unit 'airflowUnit' */
    extractAirflow: number;
    pressureDropSupply: number;
    pressureDropExtract: number;
    pressureDropOutdoor: number;
    pressureDropExhaust: number;
    airflowUnit: AirflowUnit;
    pressureUnit: PressureDropUnit;
    coil: Coil;
    dampers?: Damper[];
};

const defaultInputs: InputFields = {
    supplyAirflow: 1,
    extractAirflow: 1,
    pressureDropSupply: 150,
    pressureDropExtract: 150,
    pressureDropOutdoor: 50,
    pressureDropExhaust: 50,
    airflowUnit: defaultAirflowUnit,
    pressureUnit: defaultPressureUnit,
    coil: 'none'
};

const defaultAhu: AirHandlingUnitLite = {
    calculationStatus: CALCULATION_STATUS.CALC_NEEDS_REFRESH,
    errorMessage: '',
    calculationJobId:
        sessionStorage.getItem(STORAGE_CALC_JOB_ID_KEY) ?? undefined
};

type HomeProps = {
    market: string;
};

const Home = ({ market }: HomeProps) => {
    // #region Contexts
    const { l } = useContext(TranslationContext);
    const { user, isAuthenticated, login } = useContext(AuthContext);
    const { backendApi } = useContext(BackendApiContext);
    // #endregion

    // #region Defaults
    const searchParams = new URLSearchParams(window.location.search);
    const queryParams = getLiteQueryParams(searchParams);
    const productVariant = queryParams.productVariant || defaultUnitName;

    const derivedDefaultInput: InputFields = {
        airflowUnit: defaultAirflowUnit,
        pressureUnit: defaultPressureUnit,
        extractAirflow: Number(
            queryParams.extractAirflow ?? defaultInputs.extractAirflow
        ),
        supplyAirflow: Number(
            queryParams.supplyAirflow ?? defaultInputs.supplyAirflow
        ),
        pressureDropExhaust: Number(
            queryParams.pressureDropExhaust ?? defaultInputs.pressureDropExhaust
        ),
        pressureDropExtract: Number(
            queryParams.pressureDropExtract ?? defaultInputs.pressureDropExtract
        ),
        pressureDropOutdoor: Number(
            queryParams.pressureDropOutdoor ?? defaultInputs.pressureDropOutdoor
        ),
        pressureDropSupply: Number(
            queryParams.pressureDropSupply ?? defaultInputs.pressureDropSupply
        ),
        coil: (queryParams.coil as Coil) ?? defaultInputs.coil,
        dampers: queryParams.dampers as Damper[]
    };

    const derivedSelectedContryAndCity: SelectedCountryAndCity = {
        selectedCityKey: queryParams.climateCity
            ? Number(queryParams.climateCity)
            : NO_COUNTRY_OR_CITY_SET_YET,
        selectedCountryKey: queryParams.climateCountry
            ? Number(queryParams.climateCountry)
            : NO_COUNTRY_OR_CITY_SET_YET
    };
    // #endregion

    // #region Hooks

    const {
        data: productVariantData,
        loading: productVariantDataLoading,
        error: productVariantDataError
    } = useProductVariantData(
        backendApi,
        productVariant,
        market,
        Number(queryParams.airflow),
        queryParams.airflowUnit
            ? (AirflowUnitMap[queryParams.airflowUnit] ??
                  defaultAirflowUnit.key)
            : defaultAirflowUnit.key
    );

    const {
        data: climateLocations,
        loading: climateLocationsLoading,
        error: climateLocationsError
    } = useClimateLocations(backendApi, market);

    const conversion = useMemo(
        () =>
            new Conversion(
                (productVariantData?.allMeasurementUnitsData as MeasurementUnitsDto) ??
                    ({} as MeasurementUnitsDto)
            ),
        [productVariantData?.allMeasurementUnitsData]
    );

    // #endregion

    // #region States

    const [inputFields, setInputFields] =
        useState<InputFields>(derivedDefaultInput);

    const [selectedCountryAndCity, setSelectedCountryAndCity] =
        useState<SelectedCountryAndCity>(derivedSelectedContryAndCity);

    const [selectedProductVariantSizeId, setSelectedProductVariantSizeId] =
        useState<string | undefined>(undefined);
    const [ahu, setAhu] = useState<AirHandlingUnitLite>(defaultAhu);

    const [eventStore, setEventStore] = useState<EventStore>();

    const [productVariantSizes, setProductVariantSizes] = useState<
        ProductVariantSizesDto | undefined
    >(undefined);

    const [
        productVariantSizesWithinAirflowRange,
        setProductVariantSizesWithinAirflowRange
    ] = useState<SimpleProductVariantSize[]>([]);

    // #endregion

    // #region Derived state

    const selectedProductVariantSize = productVariantSizes?.sizes.find(
        (size) => size.sizeId === selectedProductVariantSizeId
    );

    // #endregion

    // #region Side-effects

    useEffect(() => {
        if (climateLocations) {
            setSelectedCountryAndCity((previousState) => {
                // Only change if we have default state
                // i.e C/C was not set via query params
                if (
                    previousState.selectedCityKey ===
                        NO_COUNTRY_OR_CITY_SET_YET &&
                    previousState.selectedCountryKey ===
                        NO_COUNTRY_OR_CITY_SET_YET
                ) {
                    return {
                        selectedCityKey:
                            climateLocations.defaultCountry.defaultCity.key,
                        selectedCountryKey: climateLocations.defaultCountry.key
                    };
                } else {
                    return previousState;
                }
            });
        }
    }, [climateLocations]);

    useEffect(() => {
        if (!productVariantData) {
            return;
        }

        const airflowUnitKey = queryParams.airflowUnit
            ? (AirflowUnitMap[queryParams.airflowUnit] ??
              productVariantData.guiUnitsDto.defaultAirflowUnitForMarket)
            : productVariantData.guiUnitsDto.defaultAirflowUnitForMarket;
        setInputFields((previousInputFields) => ({
            ...previousInputFields,
            airflowUnit: {
                translated:
                    productVariantData.guiUnitsDto.airflowUnitsMap[
                        airflowUnitKey
                    ],
                key: airflowUnitKey
            },
            pressureUnit: {
                translated:
                    productVariantData.guiUnitsDto.translatedPressureUnit,
                key: productVariantData.guiUnitsDto.pressureUnit
            },
            supplyAirflow: queryParams.supplyAirflow
                ? Number(queryParams.supplyAirflow)
                : conversion.convertFromM3s(
                      airflowUnitKey,
                      productVariantData.guiUnitsDto.airflowValuesDto.supply
                  ),
            extractAirflow: queryParams.extractAirflow
                ? Number(queryParams.extractAirflow)
                : conversion.convertFromM3s(
                      airflowUnitKey,
                      productVariantData.guiUnitsDto.airflowValuesDto.extract
                  ),
            pressureDropExhaust: Number(
                queryParams.pressureDropExhaust ??
                    productVariantData.guiUnitsDto.pressureDropValuesDto.exhaust
            ),
            pressureDropExtract: Number(
                queryParams.pressureDropExtract ??
                    productVariantData.guiUnitsDto.pressureDropValuesDto.extract
            ),
            pressureDropSupply: Number(
                queryParams.pressureDropSupply ??
                    productVariantData.guiUnitsDto.pressureDropValuesDto.supply
            ),
            pressureDropOutdoor: Number(
                queryParams.pressureDropOutdoor ??
                    productVariantData.guiUnitsDto.pressureDropValuesDto.outdoor
            )
        }));

        setProductVariantSizes({
            ...productVariantData.productVariantSizesDto,
            sizes: productVariantData.productVariantSizesDto.sizes.map(
                (size) => ({
                    ...size,
                    supplyAirflow: size.supplyAirflow && {
                        ...size.supplyAirflow,
                        max: conversion.convertFromM3s(
                            airflowUnitKey,
                            size.supplyAirflow?.max ?? 1
                        ),
                        min: conversion.convertFromM3s(
                            airflowUnitKey,
                            size.supplyAirflow?.min ?? 1
                        )
                    },
                    extractAirflow: size.extractAirflow && {
                        ...size.extractAirflow,
                        max: conversion.convertFromM3s(
                            airflowUnitKey,
                            size.extractAirflow?.max ?? 1
                        ),
                        min: conversion.convertFromM3s(
                            airflowUnitKey,
                            size.extractAirflow?.min ?? 1
                        )
                    }
                })
            )
        });

        const selectedSizeFromResponse =
            productVariantData?.productVariantSizesDto.sizes.find(
                (size) =>
                    size.sizeId ===
                    (queryParams.size ??
                        productVariantData?.productVariantSizesDto
                            .selectedSizeId)
            );

        if (selectedSizeFromResponse) {
            setSelectedProductVariantSizeId(selectedSizeFromResponse.sizeId);
        }
    }, [
        queryParams.extractAirflow,
        queryParams.pressureDropExhaust,
        queryParams.pressureDropExtract,
        queryParams.pressureDropOutdoor,
        queryParams.pressureDropSupply,
        queryParams.size,
        queryParams.supplyAirflow,
        productVariantData,
        queryParams.airflowUnit,
        conversion
    ]);

    useEffect(() => {
        if (!isAuthenticated() || !user?.access_token) {
            return;
        }

        let missingValues: boolean = false;
        const [websocketUrl, userId, token] = guard(
            () => {
                missingValues = true;
            },
            getConfig().websocket_url,
            user?.userId,
            user?.access_token
        ) as [string, string, string];
        if (missingValues) {
            return;
        }

        WebsocketWorker.postMessage({
            task: WEBSOCKET_ACTIONS.INITIALIZE,
            userId: userId,
            websocketUrl: websocketUrl,
            routingKey: userId,
            token: token
        });

        const eventStore = new EventStore(WebsocketWorker);
        setEventStore(eventStore);

        return () =>
            WebsocketWorker.postMessage({
                task: WEBSOCKET_ACTIONS.DISCONNECT,
                userId: userId,
                websocketUrl: websocketUrl,
                routingKey: userId,
                token: token
            });
    }, [user, isAuthenticated]);

    useEffect(() => {
        if (!productVariantData || !productVariantSizes) {
            return;
        }
        const ahuSizesWithinRange = getProductVariantSizesWithinAirflowRange(
            productVariantSizes?.sizes,
            inputFields.supplyAirflow,
            inputFields.extractAirflow
        );
        setProductVariantSizesWithinAirflowRange(
            ahuSizesWithinRange.map((size) => ({
                sizeName: size.sizeName,
                sizeId: size.sizeId
            }))
        );
    }, [
        inputFields.supplyAirflow,
        inputFields.extractAirflow,
        productVariantSizes,
        productVariantData
    ]);

    // #endregion

    // #region Helpers

    /**
     * Set the calculation status to CALC_NEEDS_REFRESH if
     * the current state is not CALC_DISABLED.
     *
     * Use forceRefresh to disregard the current calculation
     * status and set CALC_NEEDS_REFRESH.
     */
    const resetAhuCalcStatusIfNotDisabled = (forceRefresh: boolean = false) => {
        setAhu((currentAhu) => ({
            ...currentAhu,
            calculationStatus: forceRefresh
                ? 'CALC_NEEDS_REFRESH'
                : currentAhu.calculationStatus === 'CALC_DISABLED'
                  ? 'CALC_DISABLED'
                  : 'CALC_NEEDS_REFRESH'
        }));
    };

    const setAhuSize = (productVariantSizeId: string) => {
        if (productVariantSizeId === selectedProductVariantSizeId) {
            return;
        }

        if (
            !sizeIsWithinAirflowRange(
                productVariantSizeId,
                productVariantSizesWithinAirflowRange
            )
        ) {
            setInputFields((previousInputFields) => {
                const { supplyAirflow, extractAirflow } = previousInputFields;
                const closestAirflows = getClosestAirflowsForSizeOutOfRange(
                    productVariantSizeId,
                    productVariantSizes?.sizes ?? [],
                    supplyAirflow,
                    extractAirflow
                );
                return {
                    ...previousInputFields,
                    supplyAirflow: closestAirflows?.newSupplyAirflow || 1,
                    extractAirflow: closestAirflows?.newExtractAirflow || 1
                };
            });
        }

        setSelectedProductVariantSizeId(productVariantSizeId);
        resetAhuCalcStatusIfNotDisabled(true);
    };

    // #endregion

    // #region Event handlers

    const startCalculation = async () => {
        if (
            !backendApi ||
            !selectedCountryAndCity ||
            !selectedProductVariantSizeId ||
            !productVariantData
        ) {
            return;
        }

        setAhu((currentAhu) => ({
            ...currentAhu,
            calculationStatus: CALCULATION_STATUS.CALC_RUNNING
        }));

        if (!selectedProductVariantSize) {
            return;
        }
        const calculationRequestBody: SelectedValuesDto = {
            market: market,
            productVariant: productVariant,
            sizeId: selectedProductVariantSizeId,
            sizeName: selectedProductVariantSize.sizeName,
            sizeNamePadded: selectedProductVariantSize.sizeNamePadded,
            airflowUnit: inputFields.airflowUnit.key,
            supply: {
                airflow: inputFields.supplyAirflow,
                pressureDrop: inputFields.pressureDropSupply
            },
            extract: {
                airflow: inputFields.extractAirflow,
                pressureDrop: inputFields.pressureDropExtract
            },
            outdoorPressureDrop: inputFields.pressureDropOutdoor,
            exhaustPressureDrop: inputFields.pressureDropExhaust,
            climateLocation: {
                countryKey: selectedCountryAndCity.selectedCountryKey,
                cityKey: selectedCountryAndCity.selectedCityKey
            },
            coil: inputFields.coil,
            dampers: inputFields.dampers ?? []
        };

        await backendApi
            .startCalculation(calculationRequestBody)
            .then((jobIdResponse: string) => {
                setAhu((prevAhu) => ({
                    ...prevAhu,
                    calculationJobId: jobIdResponse
                }));
                addCalculationJobIdToSessionStorage(jobIdResponse);
            })
            .catch((_error) => {
                // Add user facing error handling in future release
            });
    };

    const sendEmail = (email: EmailData) => {
        if (backendApi?.sendEmail == undefined) {
            return;
        }

        backendApi
            ?.sendEmail(
                market,
                email.name,
                email.emailAddress,
                email.phone,
                email.message,
                createShareUrlFromState(),
                email.country,
                email.city,
                email.companyName,
                email.typeOfBusiness,
                {
                    productVariant:
                        productVariantData?.productVariantBasicInfoDto.title ??
                        'unknown product',
                    sizeName:
                        selectedProductVariantSize?.sizeName ?? 'unknown size'
                }
            )
            .then(() =>
                successToastr(
                    l('ahuDesignLite.share.contactSales.email.sendSuccess')
                )
            )
            .catch(() => {
                errorToastr(
                    l('ahuDesignLite.share.contactSales.email.sendError')
                );
            });
    };

    const createShareUrlFromState = (): string => {
        const paramsToShare: ProductVariantDataSearchParamsForShare = {
            productVariant: productVariant,
            size: selectedProductVariantSizeId ?? '',
            airflowUnit:
                Object.keys(AirflowUnitMap).find(
                    (key) => AirflowUnitMap[key] === inputFields.airflowUnit.key
                ) ?? defaultAirflowUnit.key,
            extractAirflow: inputFields.extractAirflow.toString(),
            supplyAirflow: inputFields.supplyAirflow.toString(),
            pressureDropExhaust: inputFields.pressureDropExhaust.toString(),
            pressureDropExtract: inputFields.pressureDropExtract.toString(),
            pressureDropOutdoor: inputFields.pressureDropOutdoor.toString(),
            pressureDropSupply: inputFields.pressureDropSupply.toString(),
            climateCity: selectedCountryAndCity.selectedCityKey.toString(),
            climateCountry:
                selectedCountryAndCity.selectedCountryKey.toString(),
            market: market,
            coil: inputFields.coil
        };
        const urlSearchParmsForShare = new URLSearchParams(paramsToShare);
        if (inputFields.dampers) {
            inputFields.dampers.forEach((damper) => {
                urlSearchParmsForShare.append('dampers', damper);
            });
        }
        return `${location.href.split('?')[0]}?${urlSearchParmsForShare.toString()}`;
    };

    const onCalculationResultChanged = (
        event: CalculationResultChangeEvent
    ) => {
        if (event.status === 'CALC_RES_ERROR') {
            clearCalculationJobIdFromSessionStorage;
        }
        setAhu((currentAhu) => ({
            ...currentAhu,
            calculationStatus: event.status
        }));
    };

    const onSelectecCountryAndCityChange = (value: SelectedCountryAndCity) => {
        if (
            selectedCountryAndCity?.selectedCityKey !== value.selectedCityKey ||
            selectedCountryAndCity.selectedCountryKey !==
                value.selectedCountryKey
        ) {
            resetAhuCalcStatusIfNotDisabled();
        }
        setSelectedCountryAndCity(value);
    };

    const onInputChange = (values: InputFields, inputErrors: boolean) => {
        setInputFields(values);
        if (inputErrors) {
            setAhu((currentAhu) => ({
                ...currentAhu,
                calculationStatus: 'CALC_DISABLED'
            }));
        } else {
            // We want to force to refresh state here and not keep
            // the disabled state
            resetAhuCalcStatusIfNotDisabled(true);
        }
    };

    const onClickCustomize = async () => {
        if (!user || !eventStore) {
            return;
        }

        const jobId = getCalculationJobIdFromSessionStorage();
        const ahuDesignUrl = getConfig().ahu_design_url ?? '';
        if (
            jobId &&
            calculationResultIsOKWithWarningsAndErrors(ahu.calculationStatus)
        ) {
            const completeAhuDesignUrl = ahuDesignUrl
                ? `${ahuDesignUrl}?ahudLiteId=${jobId}`
                : '';
            window.open(completeAhuDesignUrl, '_blank', 'noreferrer');
            return;
        }
        if (
            !backendApi ||
            !selectedCountryAndCity ||
            !selectedProductVariantSizeId ||
            !productVariantData
        ) {
            window.open(ahuDesignUrl, '_blank', 'noreferrer');
            return;
        }

        if (!selectedProductVariantSize) {
            return;
        }

        const createLiteAhuJobDto: SelectedValuesDto = {
            market: market,
            productVariant: productVariant,
            sizeId: selectedProductVariantSizeId,
            sizeName: selectedProductVariantSize.sizeName,
            sizeNamePadded: selectedProductVariantSize.sizeNamePadded,
            airflowUnit: inputFields.airflowUnit.key,
            supply: {
                airflow: inputFields.supplyAirflow,
                pressureDrop: inputFields.pressureDropSupply
            },
            extract: {
                airflow: inputFields.extractAirflow,
                pressureDrop: inputFields.pressureDropExtract
            },
            outdoorPressureDrop: inputFields.pressureDropOutdoor,
            exhaustPressureDrop: inputFields.pressureDropExhaust,
            climateLocation: {
                countryKey: selectedCountryAndCity.selectedCountryKey,
                cityKey: selectedCountryAndCity.selectedCityKey
            },
            coil: inputFields.coil,
            dampers: inputFields.dampers ?? []
        };

        const windowToOpen = window.open(
            window.origin + '/light/loading',
            '_blank'
        );
        if (windowToOpen) {
            await backendApi
                .createLiteAhuJob(createLiteAhuJobDto)
                .then((jobId) => {
                    const userIdGuest = `${user.userId}-guest`;
                    eventStore.addEventListener(
                        `${userIdGuest}.ahuCreated`,
                        (event: MessageEvent) => {
                            const { task, message } = event.data;
                            if (
                                task === WEBSOCKET_ACTIONS.AHU_CREATED &&
                                message.jobId === jobId
                            ) {
                                const completeAhuDesignUrl = ahuDesignUrl
                                    ? `${ahuDesignUrl}?ahudLiteId=${jobId}`
                                    : '';
                                windowToOpen.location.href =
                                    completeAhuDesignUrl;
                                eventStore?.removeEventListener(
                                    `${userIdGuest}.ahuCreated`
                                );
                            }
                            return;
                        }
                    );
                })
                .catch(() => {
                    errorToastr(l('ahuDesignLite.customizeInAhud.error'));
                });
        }
    };

    const onAirflowUnitChange = (newUnitKey: string) => {
        if (newUnitKey === inputFields.airflowUnit.key) {
            return;
        }

        setInputFields((previousInputFields) => ({
            ...previousInputFields,
            airflowUnit: productVariantData
                ? {
                      key: newUnitKey as AirflowUnitKey,
                      translated:
                          productVariantData.guiUnitsDto.airflowUnitsMap[
                              newUnitKey
                          ]
                  }
                : defaultAirflowUnit,
            supplyAirflow: conversion.convertFromAnyToAny(
                inputFields.airflowUnit.key,
                newUnitKey,
                previousInputFields.supplyAirflow
            ),
            extractAirflow: conversion.convertFromAnyToAny(
                inputFields.airflowUnit.key,
                newUnitKey,
                previousInputFields.extractAirflow
            )
        }));

        setProductVariantSizes(
            (previousSizes) =>
                previousSizes && {
                    ...previousSizes,
                    sizes: previousSizes?.sizes.map((size) => ({
                        ...size,
                        supplyAirflow: size.supplyAirflow && {
                            unit: newUnitKey,
                            max: conversion.convertFromAnyToAny(
                                inputFields.airflowUnit.key,
                                newUnitKey,
                                size.supplyAirflow.max
                            ),
                            min: conversion.convertFromAnyToAny(
                                inputFields.airflowUnit.key,
                                newUnitKey,
                                size.supplyAirflow.min
                            )
                        },
                        extractAirflow: size.extractAirflow && {
                            unit: newUnitKey,
                            max: conversion.convertFromAnyToAny(
                                inputFields.airflowUnit.key,
                                newUnitKey,
                                size.extractAirflow.max
                            ),
                            min: conversion.convertFromAnyToAny(
                                inputFields.airflowUnit.key,
                                newUnitKey,
                                size.extractAirflow.min
                            )
                        }
                    }))
                }
        );
    };

    const onProductSizeSelected = (newSizeId: string) => setAhuSize(newSizeId);

    // #endregion

    // #region Renders

    if (!isAuthenticated()) {
        login(getConfig());
    }

    if (window.innerWidth < 1024) {
        return <UnsupportedScreenSize />;
    }

    if (climateLocationsError || productVariantDataError) {
        const errorMessages: string[] = [
            productVariantDataError,
            climateLocationsError
        ].filter((msg) => msg !== undefined);
        return <MissingDataPage errorMessages={errorMessages} />;
    }

    const loading =
        !isAuthenticated() ||
        productVariantDataLoading ||
        climateLocationsLoading;

    return (
        <>
            <Header>
                {!loading ? (
                    <HeaderItemsContainer>
                        <HeaderItem>
                            <UnitSelector
                                id={'airflow-unit-selection'}
                                label={l('ahuDesignLite.airflowUnitSelection')}
                                options={
                                    productVariantData
                                        ? Object.keys(
                                              productVariantData.guiUnitsDto
                                                  .airflowUnitsMap
                                          ).map((unitKey) => ({
                                              title: productVariantData
                                                  ?.guiUnitsDto.airflowUnitsMap[
                                                  unitKey
                                              ],
                                              value: unitKey
                                          }))
                                        : undefined
                                }
                                selectedUnit={inputFields.airflowUnit.key}
                                onUnitChange={onAirflowUnitChange}
                            />
                        </HeaderItem>
                        <HeaderItemDivider />
                        <HeaderItem>
                            <Print
                                eventStore={eventStore}
                                market={market}
                                airflowUnit={inputFields.airflowUnit.key}
                                user={user}
                                calculationJobId={getCalculationJobIdFromSessionStorage()}
                                printButtonDisabled={
                                    [
                                        'CALC_RES_OK',
                                        'CALC_RES_OK_WITH_WARNINGS'
                                    ].includes(ahu.calculationStatus) === false
                                }
                                productVariant={productVariant}
                            />
                        </HeaderItem>
                    </HeaderItemsContainer>
                ) : null}
            </Header>
            {!loading ? (
                <ContentContainer>
                    <LeftContainer>
                        <ProductVariantBasicInfo
                            selectedProductVariantSize={productVariantSizes?.sizes.find(
                                (size) =>
                                    size.sizeId === selectedProductVariantSizeId
                            )}
                            title={
                                productVariantData?.productVariantBasicInfoDto
                                    ?.title ?? ''
                            }
                            imageUrl={
                                productVariantData?.productVariantBasicInfoDto
                                    ?.imageUrl ?? ''
                            }
                            taglines={
                                productVariantData?.productVariantBasicInfoDto
                                    ?.taglines ?? []
                            }
                        />
                        <ContactAndCustomize
                            productInfoUrl={
                                productVariantData?.productVariantBasicInfoDto
                                    ?.productInfoUrl ?? ''
                            }
                            onClickCustomize={onClickCustomize}
                            onSendEmail={sendEmail}
                            shareUrl={createShareUrlFromState()}
                            countries={
                                climateLocations?.countries.filter((c) =>
                                    contactFormMarkets.includes(c.key)
                                ) ?? []
                            }
                            preSelectedContactCountry={
                                selectedCountryAndCity.selectedCountryKey
                            }
                        />
                        <CalculationDashboard
                            ahuCalculationStatus={ahu.calculationStatus}
                            eventStore={eventStore}
                            onCalculateButtonClick={startCalculation}
                            calculationJobId={
                                ahu.calculationJobId ??
                                sessionStorage.getItem(
                                    STORAGE_CALC_JOB_ID_KEY
                                ) ??
                                undefined
                            }
                            onCalculationResultChanged={
                                onCalculationResultChanged
                            }
                        />
                    </LeftContainer>
                    <RightContainer>
                        <InputFieldsContainer
                            inputFields={inputFields}
                            selectedCountryAndCity={selectedCountryAndCity}
                            climateLocations={climateLocations}
                            onSelectedCountryOrCityChanged={
                                onSelectecCountryAndCityChange
                            }
                            airflowBounds={{
                                supplyAirflowMin:
                                    selectedProductVariantSize?.supplyAirflow
                                        ?.min,
                                supplyAirflowMax:
                                    selectedProductVariantSize?.supplyAirflow
                                        ?.max,
                                extractAirflowMin:
                                    selectedProductVariantSize?.extractAirflow
                                        ?.min,
                                extractAirflowMax:
                                    selectedProductVariantSize?.extractAirflow
                                        ?.max
                            }}
                            onInputChange={onInputChange}
                            coilAndDamperOptions={{
                                coils:
                                    productVariantData
                                        ?.productVariantCoilAndDampersDto
                                        .hasSelectableCoil ?? true,
                                dampers: productVariantData
                                    ?.productVariantCoilAndDampersDto
                                    .dampers ?? ['exhaust', 'outdoor']
                            }}
                            supplyDisabled={
                                selectedProductVariantSize?.supplyAirflow ===
                                null
                            }
                            extractDisabled={
                                selectedProductVariantSize?.extractAirflow ===
                                null
                            }
                        />
                        <SizeSelection
                            productVariantSizes={productVariantSizes}
                            productVariantSizesWithinAirflowRange={productVariantSizesWithinAirflowRange.map(
                                (size) => size.sizeName
                            )}
                            selectedProductVariantSize={
                                selectedProductVariantSizeId
                            }
                            onSizeSelected={onProductSizeSelected}
                            supplyAirflowInput={inputFields.supplyAirflow}
                            extractAirflowInput={inputFields.extractAirflow}
                            heatRecoveryType=""
                            passivhausCompatible={false}
                            designViewMode={false}
                            translatedUnit={inputFields.airflowUnit.translated}
                        />
                    </RightContainer>
                </ContentContainer>
            ) : (
                <Loading />
            )}
        </>
    );

    // #endregion
};

export default Home;
